import React from 'react'
import { collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { ref, uploadBytesResumable } from 'firebase/storage'
import { useDropzone } from 'react-dropzone'
import imageCompression from 'browser-image-compression'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { firestore, storage } from '../../../../../utils/firebase'
import MyProfileCamera from './MyProfileCamera'
import IconPicker from '../../../apps/AppComponents/IconPicker/IconPicker'

function ProfileDragDrop({
  classes,
  dispatch,
  closeDialog,
  showProfileAvatar,
  updateShowAvatar,
  fetchMyProfile,
}) {
  const [image, updateImage] = React.useState('')
  const [crop, setCrop] = React.useState({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  })

  const [imagePreview, updateImagePreview] = React.useState(null)
  const [showCameraPreview, updateShowCameraPreview] = React.useState(false)
  const imgRef = React.useRef()
  const userId = localStorage.getItem('rutgersEduRCPID')
  const handleHideAvatar = () => {
    const userDocRef = doc(
      collection(firestore, 'users'),
      localStorage.getItem('user') ?? 'visitor',
      'settings',
      'userState',
    )
    updateShowAvatar(!showProfileAvatar)
    getDoc(userDocRef).then(() => {
      setDoc(
        userDocRef,
        { showProfileAvatar: !showProfileAvatar },
        { merge: true },
      )
    })
    dispatch(closeDialog())
  }

  const createAndReturnCanvas = () => {
    const canvas = document.createElement('canvas')
    const imgCropped = imgRef.current
    const scaleX = imgCropped.naturalWidth / imgCropped.width
    const scaleY = imgCropped.naturalHeight / imgCropped.height
    canvas.width = crop.width
    canvas.height = crop.height

    const ctx = canvas.getContext('2d')
    ctx.drawImage(
      imgCropped,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height,
    )

    return canvas
  }

  const handleRestoreIDPhoto = () => {
    const userDocRef = doc(
      collection(firestore, 'users'),
      localStorage.getItem('user') ?? 'visitor',
      'userInfo',
      'Info',
    )
    updateDoc(userDocRef, {
      avatar: `/profilePictures/${userId}_IDPhoto.png`,
    }).then(() => {
      dispatch(fetchMyProfile())
        .unwrap()
        .then(() => {
          dispatch(closeDialog())
        })
    })
  }
  const onDrop = React.useCallback((acceptedFiles) => {
    console.log('calling ondrop')
    acceptedFiles.forEach(async (file) => {
      // convert file to jpg
      const reader = new FileReader()
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 400,
      })
      // console.log('compressed file ', compressedFile)
      reader.onabort = () => console.log('file reading was aborted')
      reader.onerror = () => console.log('file reading has failed')
      reader.onload = () => {
        const binaryStr = reader.result
        const blob = new Blob([binaryStr])
        const srcBlob = URL.createObjectURL(blob)
        updateImage(srcBlob)
        let uploadTask
        if (userId) {
          const storageRef = ref(
            storage,
            `/profilePictures/${userId}_CustomAvatar.png`,
          )
          uploadTask = uploadBytesResumable(storageRef, compressedFile)
          const userDocRef = doc(
            collection(firestore, 'users'),
            localStorage.getItem('user') ?? 'visitor',
            'userInfo',
            'Info',
          )
          // update user document collection - avatar example profilePictures/891430_CustomAvatar.jpg
          updateDoc(userDocRef, {
            avatar: `/profilePictures/${userId}_CustomAvatar.png`,
          })
        }

        // Listen for state changes, errors, and completion of the upload.
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            console.log(`Upload is ${progress} % done`)
            switch (snapshot.state) {
              case 'paused':
                console.log('Upload is paused')
                break
              case 'running':
                console.log('Upload is running')
                break
              default:
                console.log('upload failed')
                break
            }
          },
          (error) => {
            console.log('error ', error)
          },
          () => {
            // Upload completed successfully, now we can get the download URL
            console.log('upload completed successfully')
          },
        )
      }
      reader.readAsArrayBuffer(file)
    })
  }, [])
  const handleDoneCropping = () => {
    const canvas = createAndReturnCanvas()

    canvas.toBlob((blob) => {
      onDrop([blob]) // RE-TRIGGER onDrop
      dispatch(fetchMyProfile())
        .unwrap()
        .then(() => {
          dispatch(closeDialog())
        })
    }, 'image/jpeg')
  }
  const handleOnChange = (c) => {
    setCrop(c)
  }
  const handleOnComplete = () => {
    const canvas = createAndReturnCanvas()

    canvas.toBlob((blob) => {
      const srcBlob = URL.createObjectURL(blob)
      updateImagePreview(srcBlob)
    }, 'image/jpeg')
  }

  const handleShowCameraPreview = () => {
    updateShowCameraPreview(!showCameraPreview)
  }
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
  })

  // logic needed to center the square crop box on the image when it loads
  function onImageLoad(e) {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget

    const newCrop = centerCrop(
      makeAspectCrop(
        {
          // You don't need to pass a complete crop into
          // makeAspectCrop or centerCrop.
          unit: '%',
          width: 50,
        },
        1 / 1,
        width,
        height,
      ),
      width,
      height,
    )

    setCrop(newCrop)
  }

  return (
    <>
      {image && !showCameraPreview ? (
        <Box display="flex" flexDirection="column" alignItems="center">
          <ReactCrop
            crop={crop}
            onChange={handleOnChange}
            onComplete={handleOnComplete}
            className={classes.cropContainer}
            aspect={1 / 1} // ensures the crop is always a square
          >
            <img
              className={classes.cropImage}
              ref={imgRef}
              src={image}
              onLoad={onImageLoad}
              alt="Crop"
            />
          </ReactCrop>
          <Button
            className={classes.cropNowActionButton}
            onClick={handleDoneCropping}
            variant="contained"
            // color="primary"
          >
            <div className={classes.cropIcon}>
              <IconPicker iconName="fa-solid fa-crop" size="1x" />
            </div>
            <Typography variant="h5">Crop Now</Typography>
          </Button>
        </Box>
      ) : null}
      {showCameraPreview && image === '' ? (
        <MyProfileCamera
          classes={classes}
          updateImagePreview={updateImagePreview}
          updateImage={updateImage}
          showCameraPreview={showCameraPreview}
          updateShowCameraPreview={updateShowCameraPreview}
        />
      ) : null}
      {imagePreview && (
        <Box
          display="flex"
          flexDirecton="column"
          alignItems="center"
          justifyContent="center"
          className={classes.imagePreviewContainer}
        >
          <Typography variant="subtitle1">Preview Profile Picture: </Typography>
          <img
            src={imagePreview}
            className={classes.profilePicture}
            alt="profile avatar"
            style={{ marginLeft: 16 }}
          />
        </Box>
      )}
      <Box pb={1}>
        <Typography variant="subtitle1">
          Upload a custom avatar from your device. Image should be square, at
          least 184px x 184px and .png or .jpg format.
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="center">
        {showCameraPreview || image ? null : (
          <Box display="flex" justifyContent="space-evenly" alignItems="center">
            <Box className={classes.profileDragDropContainer}>
              <Button
                className={classes.idPhotoActionButton}
                onClick={handleShowCameraPreview}
              >
                Take a Picture
              </Button>
            </Box>
            <Typography
              variant="subtitle1"
              className={classes.dragDropRegionSpacing}
            >
              or
            </Typography>
            <Box
              className={classes.profileDragDropContainer}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <Typography variant="subtitle1">
                  Drop the files here ...
                </Typography>
              ) : (
                <Button className={classes.idPhotoActionButton}>
                  Upload Custom Photo.
                </Button>
              )}
            </Box>
          </Box>
        )}
        <Box className={classes.buttonsContainer}>
          <Button
            className={classes.idPhotoHelperButton}
            variant="contained"
            onClick={handleRestoreIDPhoto}
          >
            <Typography variant="body1">Restore ID Photo</Typography>
          </Button>
          <Button
            className={classes.idPhotoHelperButtonSecondary}
            color="secondary"
            variant="contained"
            // variant="outlined"
            onClick={handleHideAvatar}
          >
            <Typography variant="body1">
              {showProfileAvatar ? 'Hide Photo' : 'Show Photo'}
            </Typography>
          </Button>
        </Box>
      </Box>
    </>
  )
}

export default ProfileDragDrop
